import { lazy, Suspense } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import {
  Droplet,
  Disc3,
  CircleDot,
  Phone,
  Clock,
  ShieldCheck,
  MapPin,
  ArrowRight,
  Wrench,
  Gauge,
  Star,
  TrendingDown,
  Check,
  X,
} from "lucide-react";

const BookingForm = lazy(() => import("@/components/BookingForm"));

const HERO_IMG = "/images/hero.webp";
const OIL_IMG = "/images/oil.webp";
const BRAKE_IMG = "/images/brake.webp";
const TIRE_IMG = "/images/tire.webp";
const ATL_IMG = "/images/atlanta.webp";

const services = [
  {
    icon: Droplet,
    title: "Mobile Oil Changes",
    img: OIL_IMG,
    desc: "Full-synthetic and conventional oil changes performed on-site. Filters, top-offs, and a multi-point inspection every time.",
    points: ["Engine & filter service", "Multi-point inspection", "Fleet oil tracking"],
  },
  {
    icon: CircleDot,
    title: "Mobile Tire Maintenance and Repair",
    img: TIRE_IMG,
    desc: "Mounting, balancing, rotations, pressure checks, and roadside replacements to keep every rig safe and rolling.",
    points: ["Rotation & balancing", "Pressure & tread checks", "Roadside replacement"],
  },
  {
    icon: Disc3,
    title: "Mobile Brake Maintenance",
    img: BRAKE_IMG,
    desc: "Pad, shoe, and drum service plus air-brake inspections by certified techs — DOT-ready and dialed in.",
    points: ["Pads, shoes & drums", "Air-brake inspection", "DOT compliance"],
  },
];

const steps = [
  { n: "01", title: "Request Service", desc: "Tell us your truck, location, and what it needs. Takes under two minutes." },
  { n: "02", title: "We Dispatch", desc: "A certified mobile tech is routed to your yard, truck stop, or roadside." },
  { n: "03", title: "Back On The Road", desc: "Service done on-site. You approve pricing first, then get rolling fast." },
];

const benefits = [
  { icon: Clock, title: "Minimize Downtime", desc: "We come to you — no shop waits, no towing, no lost loads." },
  { icon: ShieldCheck, title: "Certified Techs", desc: "DOT-savvy, fully-equipped technicians for heavy-duty rigs." },
  { icon: Gauge, title: "Fast Response", desc: "Same-day and emergency service across metro Atlanta." },
  { icon: Wrench, title: "Fleet Programs", desc: "Scheduled PM plans and reporting for fleets of any size." },
];

const testimonials = [
  { name: "Marcus T.", role: "Owner-Operator", text: "Blew a tire off I-285 at 5am. F1 had a tech to me in 40 minutes. Lifesavers." },
  { name: "Diana R.", role: "Fleet Manager, 22 trucks", text: "Their PM program keeps our whole fleet compliant. Reporting is clean and on time." },
  { name: "Sam K.", role: "Owner-Operator", text: "On-site oil change at the truck stop while I slept. Woke up ready to roll." },
];

const shopCosts = [
  { label: "Deadhead drive to & from the shop", value: "1 – 3 hrs" },
  { label: "Waiting for service", value: "2 – 4 hrs" },
  { label: "HOS clock burning while parked", value: "Lost drive time" },
  { label: "Lost revenue (half-day off the road)", value: "$500 – $700" },
];

const mobileCosts = [
  { label: "We come to your yard or stop", value: "No drive hours wasted" },
  { label: "Service while you load or rest", value: "No waiting" },
  { label: "Stay on your hours & schedule", value: "Keep driving" },
  { label: "No revenue lost to a shop trip", value: "$0 lost" },
];

export default function LandingPage() {
  return (
    <div id="top" className="bg-background">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img src={HERO_IMG} srcSet={`/images/hero-sm.webp 800w, /images/hero.webp 1500w`} sizes="100vw" alt="Red semi truck on highway" className="w-full h-full object-cover" fetchPriority="high" width="1500" height="922" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-black/20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 w-full">
          <div className="max-w-3xl animate-fade-up">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-white bg-primary px-3 py-1.5 rounded-sm mb-6">
              <MapPin className="w-3.5 h-3.5" /> Serving Atlanta & Surrounding Areas
            </span>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-none text-white mb-6">
              MOBILE TRUCK SERVICE THAT COMES TO <span className="text-primary">YOU.</span>
            </h1>
            <p className="text-lg text-white/80 max-w-xl mb-10 leading-relaxed">
              On-site oil changes, tire maintenance, and brake repair for semi-trucks. Keep your rig
              rolling — we bring the shop to your yard, truck stop, or the roadside.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#booking"
                data-testid="hero-book-cta"
                className="bg-primary text-white px-8 py-4 text-sm font-bold uppercase tracking-widest rounded-sm hover:bg-white hover:text-secondary transition-colors duration-300 flex items-center justify-center gap-2"
              >
                Book Service <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="tel:+14046356669"
                data-testid="hero-call-cta"
                className="bg-white/10 backdrop-blur border border-white/30 text-white px-8 py-4 text-sm font-bold uppercase tracking-widest rounded-sm hover:bg-white hover:text-secondary transition-colors duration-300 flex items-center justify-center gap-2"
              >
                <Phone className="w-4 h-4" /> (404) 635-6669
              </a>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 bg-black/40 backdrop-blur">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
            {[
              ["24/7", "Emergency Dispatch"],
              ["40 min", "Avg Response"],
              ["100%", "On-Site Service"],
              ["1,200+", "Trucks Serviced"],
            ].map(([a, b]) => (
              <div key={b} className="py-5 px-4 text-center">
                <div className="text-2xl sm:text-3xl font-black text-white tracking-tight" style={{ fontFamily: "Inter" }}>
                  {a}
                </div>
                <div className="text-[11px] uppercase tracking-widest text-white/50 font-semibold mt-1">{b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B00000] mb-3">What We Do</p>
            <h2 className="text-3xl sm:text-5xl font-black tracking-tighter mb-4">
              Heavy-duty service, zero detours.
            </h2>
            <p className="text-muted-foreground text-lg">
              Three core services that keep semis safe, compliant, and earning miles — all performed
              wherever your truck sits.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-black/10">
            {services.map((s, i) => (
              <div
                key={s.title}
                data-testid={`service-card-${i}`}
                className="group border-r border-b border-black/10 bg-white hover:bg-muted/40 transition-colors duration-300"
              >
                <div className="h-52 overflow-hidden">
                  <img
                    src={s.img}
                    srcSet={`${s.img.replace(".webp", "-sm.webp")} 640w, ${s.img} 900w`}
                    sizes="(max-width: 768px) 100vw, 33vw"
                    alt={s.title}
                    loading="lazy"
                    decoding="async"
                    width="900"
                    height="604"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-8">
                  <div className="bg-primary inline-flex p-3 rounded-sm mb-5">
                    <s.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold tracking-tight mb-3">{s.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-5">{s.desc}</p>
                  <ul className="space-y-2">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-center gap-2 text-sm font-medium">
                        <span className="w-1.5 h-1.5 bg-primary" /> {p}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-24 bg-secondary text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A4D] mb-3">Why F1 Fleet</p>
              <h2 className="text-3xl sm:text-5xl font-black tracking-tighter mb-6">
                Downtime is expensive. We make it short.
              </h2>
              <p className="text-white/70 text-lg leading-relaxed">
                A parked truck doesn't earn. Our mobile crews bring shop-grade service to your
                location so you spend less time waiting and more time driving.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
              {benefits.map((b) => (
                <div key={b.title} className="bg-secondary p-8">
                  <b.icon className="w-8 h-8 text-primary mb-4" />
                  <h3 className="text-lg font-bold mb-2">{b.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B00000] mb-3">How It Works</p>
            <h2 className="text-3xl sm:text-5xl font-black tracking-tighter">Three steps to rolling again.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-black/10 border border-black/10">
            {steps.map((s) => (
              <div key={s.n} className="bg-white p-10">
                <div className="text-6xl font-black text-primary/30 tracking-tighter mb-4" style={{ fontFamily: "Inter" }}>
                  {s.n}
                </div>
                <h3 className="text-xl font-bold tracking-tight mb-3">{s.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 bg-muted/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-5xl font-black tracking-tighter mb-14">Drivers trust F1 Fleet.</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-white border border-black/10 p-8 rounded-sm">
                <div className="flex gap-1 mb-4 text-primary">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <p className="text-foreground/80 leading-relaxed mb-6">"{t.text}"</p>
                <div>
                  <div className="font-bold tracking-tight">{t.name}</div>
                  <div className="text-sm text-muted-foreground">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COST SAVINGS */}
      <section id="savings" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B00000] mb-3">The Math</p>
            <h2 className="text-3xl sm:text-5xl font-black tracking-tighter mb-4">
              A routine shop visit burns time and money.
            </h2>
            <p className="text-muted-foreground text-lg">
              An oil change or tire repair is quick work — but driving to a shop and waiting in line can
              cost you <span className="font-bold text-foreground">half a day off the road</span> and
              <span className="font-bold text-foreground"> $500–$700</span> in lost revenue. Mobile service
              comes to your truck, so the only thing that stops is the wrench.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-px bg-black/10 border border-black/10">
            {/* Shop visit */}
            <div data-testid="cost-shop" className="bg-muted/40 p-8 sm:p-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-foreground/10 p-2.5 rounded-sm">
                  <TrendingDown className="w-5 h-5 text-foreground" />
                </div>
                <h3 className="text-xl font-bold tracking-tight">Shop Visit for Oil / Tire</h3>
              </div>
              <ul className="space-y-4 mb-8">
                {shopCosts.map((c) => (
                  <li key={c.label} className="flex items-start justify-between gap-4 border-b border-black/10 pb-3">
                    <span className="flex items-start gap-2 text-sm text-foreground/80">
                      <X className="w-4 h-4 text-destructive mt-0.5 shrink-0" /> {c.label}
                    </span>
                    <span className="font-mono font-bold text-sm whitespace-nowrap">{c.value}</span>
                  </li>
                ))}
              </ul>
              <div className="flex items-end justify-between">
                <span className="text-xs uppercase tracking-widest font-bold text-muted-foreground">
                  Typical loss
                </span>
                <span className="text-3xl font-black tracking-tighter" style={{ fontFamily: "Inter" }}>
                  ½ day + $500–$700
                </span>
              </div>
            </div>

            {/* Mobile service */}
            <div data-testid="cost-mobile" className="bg-secondary text-white p-8 sm:p-10 relative">
              <span className="absolute top-6 right-6 bg-primary text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-sm">
                Smart Choice
              </span>
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-primary p-2.5 rounded-sm">
                  <Wrench className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold tracking-tight">F1 Mobile Service</h3>
              </div>
              <ul className="space-y-4 mb-8">
                {mobileCosts.map((c) => (
                  <li key={c.label} className="flex items-start justify-between gap-4 border-b border-white/10 pb-3">
                    <span className="flex items-start gap-2 text-sm text-white/80">
                      <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" /> {c.label}
                    </span>
                    <span className="font-mono font-bold text-sm whitespace-nowrap text-primary">{c.value}</span>
                  </li>
                ))}
              </ul>
              <div className="flex items-end justify-between">
                <span className="text-xs uppercase tracking-widest font-bold text-white/50">
                  You keep
                </span>
                <span className="text-3xl font-black tracking-tighter text-[#FF5A4D]" style={{ fontFamily: "Inter" }}>
                  Your day + your pay
                </span>
              </div>
            </div>
          </div>

          <p className="text-xs text-muted-foreground mt-6 max-w-3xl">
            Figures are illustrative industry estimates for a single truck and a routine oil change or tire
            repair; actual time and revenue vary by route, location, and rate-per-mile. The point stands:
            most of the cost of a shop visit isn't the service — it's the hours off the road. We confirm
            exact pricing before any work begins.
          </p>

          <div className="mt-10">
            <a
              href="#booking"
              data-testid="savings-book-cta"
              className="inline-flex items-center gap-2 bg-primary text-white px-8 py-4 text-sm font-bold uppercase tracking-widest rounded-sm hover:bg-secondary transition-colors duration-300"
            >
              Skip The Shop Trip — Book Service <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section id="service-area" className="relative py-28">
        <div className="absolute inset-0">
          <img src={ATL_IMG} srcSet={`/images/atlanta-sm.webp 900w, /images/atlanta.webp 1500w`} sizes="100vw" alt="Atlanta skyline" loading="lazy" decoding="async" width="1500" height="843" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 to-black/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A4D] mb-3">Coverage Area</p>
          <h2 className="text-3xl sm:text-5xl font-black tracking-tighter mb-6 max-w-2xl">
            Serving Atlanta & surrounding areas.
          </h2>
          <p className="text-white/80 text-lg max-w-xl mb-8">
            From I-285 to I-75, I-85, and I-20 — Marietta, Decatur, Sandy Springs, College Park, and
            beyond. If you're hauling through metro Atlanta, we'll reach you.
          </p>
          <div className="flex flex-wrap gap-3">
            {["Atlanta", "McDonough", "Stockbridge", "Locust Grove", "Hampton", "Griffin", "Jackson", "Covington", "Conyers", "Newnan", "Marietta", "Decatur", "Sandy Springs", "College Park", "Smyrna", "Stone Mountain"].map(
              (c) => (
                <span key={c} className="border border-white/30 px-4 py-2 text-sm font-semibold rounded-sm">
                  {c}
                </span>
              )
            )}
          </div>
        </div>
      </section>

      {/* BOOKING */}
      <section id="booking" className="py-24 bg-muted/40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B00000] mb-3">Book Service</p>
            <h2 className="text-3xl sm:text-5xl font-black tracking-tighter mb-4">
              Request a mobile tech.
            </h2>
            <p className="text-muted-foreground text-lg">
              Fill out the form and our Atlanta dispatch team will confirm your appointment. Need it
              now? Call <a href="tel:+14046356669" className="font-bold text-[#B00000]">(404) 635-6669</a>.
            </p>
          </div>
          <Suspense
            fallback={
              <div className="bg-white border border-black/10 shadow-xl rounded-sm p-6 sm:p-10 min-h-[640px] flex items-center justify-center text-muted-foreground text-sm">
                Loading booking form…
              </div>
            }
          >
            <BookingForm />
          </Suspense>
        </div>
      </section>

      <Footer />
    </div>
  );
}
